import { FC, useMemo } from "react";
import HorizontalSplitOutlinedIcon from "@mui/icons-material/HorizontalSplitOutlined";
import { Table, TableBody } from "@mui/material";
import { GeneralContentDetailsTableRow } from "./GeneralContentDetailsTableRow";
import { ResourceDetailsContentCollapsableCard } from "../../../ResourceDetailsContentCollapsableCard";
import { useAppSelector } from "@/store/hooks";
import { resourceDetailsTargetSelector } from "@/store/resource-details/selectors/target-resource/resourceDetailsTargetSelector";
import { TypographyWithCopy } from "@/components/common/TypographyWithCopy";
import { resourceDetailsGeneralContentDetailsFromResourceGenerator } from "@/components/pages/common/resource-details/components/content/general-content/components/details/resource-details-general-content-details-generator/resourceDetailsGeneralContentDetailsFromResourceGenerator";
import { resourceSmartTagsColumnDefColId } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/smartTagsColumnDef";
import { listingPriceColumnDefField } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/listingPriceColumnDef";
import { ResourceDetailsListingPrice } from "@/components/pages/common/resource-details/components/content/general-content/components/details/resource-details-custom-components/ResourceDetailsListingPrice";
import { CustomValueComponentIdentifierKeys } from "@/store/resource-details/utils/types/resourceAdditionalDetails";
import { resourceDetailsGeneralContentDetailsFromMappingGenerator } from "@/components/pages/common/resource-details/components/content/general-content/components/details/resource-details-general-content-details-generator/resourceDetailsGeneralContentDetailsFromMappingGenerator";

export const colDefsWithCustomComponents: Record<
  CustomValueComponentIdentifierKeys,
  FC<any>
> = {
  [listingPriceColumnDefField]: ResourceDetailsListingPrice,
};

export const ResourceDetailsGeneralContentDetails: FC = () => {
  const target = useAppSelector(resourceDetailsTargetSelector);

  const resource = target?.resource;
  const resourceAdditionalDetails = target?.resourceAdditionalDetails;

  const details = useMemo(() => {
    if (!resourceAdditionalDetails?.resource) {
      return resourceDetailsGeneralContentDetailsFromMappingGenerator(
        resourceAdditionalDetails?.details ?? [],
      );
    }

    return resourceDetailsGeneralContentDetailsFromResourceGenerator(
      resourceAdditionalDetails?.resource,
      colDefsThatNoNeedToBeInDetails,
    );
  }, [resourceAdditionalDetails]);

  if (!resource) {
    return null;
  }

  return (
    <ResourceDetailsContentCollapsableCard
      title="Details"
      Icon={<HorizontalSplitOutlinedIcon fontSize="small" />}
    >
      <Table sx={{ tableLayout: "fixed" }}>
        <TableBody>
          {details.map((detail, i) => {
            return (
              <GeneralContentDetailsTableRow
                key={detail.title}
                label={detail.title}
                highlight={i % 2 === 0}
              >
                {typeof detail.value === "string" ? (
                  <TypographyWithCopy value={detail.value} variant="body2" />
                ) : (
                  detail.value
                )}
              </GeneralContentDetailsTableRow>
            );
          })}
        </TableBody>
      </Table>
    </ResourceDetailsContentCollapsableCard>
  );
};

const colDefsThatNoNeedToBeInDetails = [
  "resource.tags",
  "resource.inline_metrics",
  "resource.task",
  "resource.issues",
  "resource.labels",
  resourceSmartTagsColumnDefColId,
];
